import { productCategoryLabel } from './constants'
import type {
  CustomerInfoItem,
  SampleFactoryAnon,
  SampleFulfilmentKind,
  SampleCostBearer,
  SampleOfferType,
  SampleOrderStatus,
  SamplePaymentStatus,
  SampleWorkStatus,
} from './schemas/sample-orders'
import type { SampleMessageStatus } from './schemas/sample-messages'
import type { Language } from './schemas/simulation'

/**
 * 샘플 거래 문구 (앱·공장 포털·관리자 공용, 4개 언어). 앱 화면 전용 긴 안내는 앱 쪽 copy 파일에 둔다.
 * 공장 이름·지역·제품명 같은 고유명사는 번역하지 않는다.
 */

export const SAMPLE_OFFER_TYPE_LABEL: Record<Language, Record<SampleOfferType, string>> = {
  ko: {
    free_sample: '무료 샘플',
    paid_prototype: '유료 시제품',
    info_for_sample: '정보 제공 샘플',
    free_tasting: '무료 시식',
    paid_consulting: '시제품 컨설팅',
    portion_kit: '소분 키트',
  },
  en: {
    free_sample: 'Free sample',
    paid_prototype: 'Paid prototype',
    info_for_sample: 'Sample for your details',
    free_tasting: 'Free tasting',
    paid_consulting: 'Prototype consulting',
    portion_kit: 'Portion kit',
  },
  ja: {
    free_sample: '無料サンプル',
    paid_prototype: '有料試作品',
    info_for_sample: '情報提供サンプル',
    free_tasting: '無料試食',
    paid_consulting: '試作コンサルティング',
    portion_kit: '小分けキット',
  },
  zh: {
    free_sample: '免费样品',
    paid_prototype: '付费试制品',
    info_for_sample: '提供信息换样品',
    free_tasting: '免费试吃',
    paid_consulting: '试制咨询',
    portion_kit: '分装试用套装',
  },
}

export const CUSTOMER_INFO_ITEM_LABEL: Record<Language, Record<CustomerInfoItem, string>> = {
  ko: {
    name: '이름',
    phone: '전화번호',
    email: '이메일',
    address: '받는 주소',
    company: '회사·브랜드 이름',
    business_number: '사업자등록번호',
  },
  en: {
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    address: 'Delivery address',
    company: 'Company or brand',
    business_number: 'Business registration number',
  },
  ja: {
    name: '氏名',
    phone: '電話番号',
    email: 'メールアドレス',
    address: '送付先住所',
    company: '会社・ブランド名',
    business_number: '事業者登録番号',
  },
  zh: {
    name: '姓名',
    phone: '电话',
    email: '电子邮箱',
    address: '收货地址',
    company: '公司或品牌名称',
    business_number: '营业执照号码',
  },
}

export const SAMPLE_ORDER_STATUS_LABEL: Record<Language, Record<SampleOrderStatus, string>> = {
  ko: {
    pending_payment: '결제 대기',
    paid: '결제 완료',
    spec_sent: '공장에 사양 전달',
    in_progress: '공장 진행 중',
    delivered: '받음',
    completed: '시식 기록 완료',
    cancelled: '취소됨',
    refund_pending: '환불 검토 중',
    refunded: '환불됨',
  },
  en: {
    pending_payment: 'Awaiting payment',
    paid: 'Paid',
    spec_sent: 'Spec sent to factory',
    in_progress: 'Factory working on it',
    delivered: 'Received',
    completed: 'Tasting recorded',
    cancelled: 'Cancelled',
    refund_pending: 'Refund under review',
    refunded: 'Refunded',
  },
  ja: {
    pending_payment: 'お支払い待ち',
    paid: 'お支払い済み',
    spec_sent: '工場へ仕様を送付',
    in_progress: '工場で対応中',
    delivered: '受け取り済み',
    completed: '試食記録済み',
    cancelled: 'キャンセル済み',
    refund_pending: '返金を確認中',
    refunded: '返金済み',
  },
  zh: {
    pending_payment: '待付款',
    paid: '已付款',
    spec_sent: '已向工厂发送规格',
    in_progress: '工厂处理中',
    delivered: '已收到',
    completed: '已记录试吃',
    cancelled: '已取消',
    refund_pending: '退款审核中',
    refunded: '已退款',
  },
}

/** 결제 상태 (대표 결정 2차 D — 업무 상태와 나눠 보인다) */
export const SAMPLE_PAYMENT_STATUS_LABEL: Record<Language, Record<SamplePaymentStatus, string>> = {
  ko: { unpaid: '결제 전', paid: '결제 완료', refund_pending: '환불 검토 중', refunded: '환불됨' },
  en: {
    unpaid: 'Unpaid',
    paid: 'Paid',
    refund_pending: 'Refund under review',
    refunded: 'Refunded',
  },
  ja: {
    unpaid: '未払い',
    paid: '支払い済み',
    refund_pending: '返金を確認中',
    refunded: '返金済み',
  },
  zh: { unpaid: '未付款', paid: '已付款', refund_pending: '退款审核中', refunded: '已退款' },
}

/** 업무 상태 */
export const SAMPLE_WORK_STATUS_LABEL: Record<Language, Record<SampleWorkStatus, string>> = {
  ko: {
    awaiting_payment: '결제 기다림',
    awaiting_info: '고객 정보 동의 기다림',
    spec_sent: '공장에 사양 전달',
    in_progress: '공장 진행 중',
    delivered: '고객이 받음',
    completed: '시식 기록 완료',
    cancelled: '취소됨',
  },
  en: {
    awaiting_payment: 'Awaiting payment',
    awaiting_info: 'Awaiting customer consent',
    spec_sent: 'Spec sent to factory',
    in_progress: 'Factory working on it',
    delivered: 'Received by customer',
    completed: 'Tasting recorded',
    cancelled: 'Cancelled',
  },
  ja: {
    awaiting_payment: 'お支払い待ち',
    awaiting_info: 'お客様の同意待ち',
    spec_sent: '工場へ仕様を送付',
    in_progress: '工場で対応中',
    delivered: 'お客様が受け取り済み',
    completed: '試食記録済み',
    cancelled: 'キャンセル済み',
  },
  zh: {
    awaiting_payment: '等待付款',
    awaiting_info: '等待客户同意',
    spec_sent: '已向工厂发送规格',
    in_progress: '工厂处理中',
    delivered: '客户已收到',
    completed: '已记录试吃',
    cancelled: '已取消',
  },
}

/** 무료 제안의 비용 부담자 (대표 결정 2차 B) */
export const SAMPLE_COST_BEARER_LABEL: Record<Language, Record<SampleCostBearer, string>> = {
  ko: { factory: '공장', recipecat: '레시피캣', shared: '나눠 냄' },
  en: { factory: 'Factory', recipecat: 'RecipeCat', shared: 'Shared' },
  ja: { factory: '工場', recipecat: 'RecipeCat', shared: '分担' },
  zh: { factory: '工厂', recipecat: 'RecipeCat', shared: '分摊' },
}

export const SAMPLE_FULFILMENT_LABEL: Record<Language, Record<SampleFulfilmentKind, string>> = {
  ko: { shipped: '발송', tasting_scheduled: '시식 일정', consulting_scheduled: '컨설팅 일정' },
  en: {
    shipped: 'Shipped',
    tasting_scheduled: 'Tasting scheduled',
    consulting_scheduled: 'Consulting scheduled',
  },
  ja: { shipped: '発送', tasting_scheduled: '試食日程', consulting_scheduled: 'コンサル日程' },
  zh: { shipped: '已发货', tasting_scheduled: '试吃安排', consulting_scheduled: '咨询安排' },
}

/** AI 중계 메시지 상태 (보낸 쪽 화면) */
export const SAMPLE_MESSAGE_STATUS_LABEL: Record<Language, Record<SampleMessageStatus, string>> = {
  ko: {
    processing: '정리해 전달하는 중',
    awaiting_customer: '정리해 전달하는 중',
    delivered: '전달됨',
    review: '운영자가 확인 중',
    blocked: '전달하지 않음',
  },
  en: {
    processing: 'Sending',
    awaiting_customer: 'Sending',
    delivered: 'Delivered',
    review: 'Being checked by RecipeCat',
    blocked: 'Not delivered',
  },
  ja: {
    processing: '整理して送信中',
    awaiting_customer: '整理して送信中',
    delivered: '送信済み',
    review: '運営者が確認中',
    blocked: '送信しませんでした',
  },
  zh: {
    processing: '正在整理并发送',
    awaiting_customer: '正在整理并发送',
    delivered: '已送达',
    review: '运营人员审核中',
    blocked: '未送达',
  },
}

/** 시식 요일 이름 0(일)~6(토) */
export const SAMPLE_WEEKDAY_LABEL: Record<Language, readonly string[]> = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ja: ['日', '月', '火', '水', '木', '金', '土'],
  zh: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
}

/** 결제 전 익명 공장 이름 — '경기 소스류 A공장', 'Gyeonggi Sauce & Seasoning factory A' */
export function sampleFactoryLabel(language: Language, anon: SampleFactoryAnon): string {
  const category = productCategoryLabel(anon.categoryId, language)
  switch (language) {
    case 'ko':
      return `${anon.region} ${category} ${anon.letter}공장`
    case 'ja':
      return `${anon.region} ${category} ${anon.letter}工場`
    case 'zh':
      return `${anon.region} ${category} ${anon.letter}工厂`
    default:
      return `${anon.region} ${category} factory ${anon.letter}`
  }
}

/** 0 → A, 25 → Z, 26 → AA. 제안을 가진 공장의 순번 글자 */
export function sampleFactoryLetter(index: number): string {
  let n = index
  let out = ''
  do {
    out = String.fromCharCode(65 + (n % 26)) + out
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return out
}

/** 공장 포털에서 고객을 가리키는 이름 — '고객 #A1B2C3' */
export function sampleCustomerRef(orderId: string): string {
  return orderId.replace(/-/g, '').slice(0, 6).toUpperCase()
}

/** 공장 포털·관리자 공용 샘플 화면 문구 (관리자는 ko 를 쓴다) */
export interface SamplePortalCopy {
  section: string
  scope: string
  offersTitle: string
  newOffer: string
  edit: string
  save: string
  saved: string
  factoryName: string
  anonRegion: string
  categories: string
  type: string
  status: string
  offerStatus: Record<'draft' | 'active' | 'paused', string>
  currency: string
  price: string
  priceHint: string
  leadDays: string
  credited: string
  requiredInfo: string
  tastingRegions: string
  tastingWeekdays: string
  consultingMinutes: string
  deliverables: string
  note: string
  /** 받는 것·메모 칸 아래 안내 — 결제 전 고객에게 보이므로 이름·연락처·주소를 쓰지 말 것 */
  publicTextHint: string
  freeCostTitle: string
  freeCostHint: string
  costBearer: string
  budgetAmount: string
  budgetQuantity: string
  freeCostNote: string
  ordersTitle: string
  noOrders: string
  customer: (ref: string) => string
  brief: string
  due: string
  specLocked: string
  formulation: string
  process: string
  cost: string
  downloadCsv: string
  downloadJson: string
  disclosed: string
  progressNote: string
  scheduledAt: string
  recordProgress: string
  failed: string
  messagesTitle: string
  messagesNote: string
  messagePlaceholder: string
  counterOffer: string
  send: string
  changes: string
}

export const SAMPLE_PORTAL_COPY: Record<Language, SamplePortalCopy> = {
  ko: {
    section: '샘플·시제품',
    scope:
      '샘플 방식과 조건을 올리면, 고객은 결제 전까지 익명 이름으로 보고 결제한 뒤에 공장 이름을 봐요. 고객과의 대화는 레시피캣이 이어 드려요.',
    offersTitle: '샘플 제안',
    newOffer: '새 제안',
    edit: '고치기',
    save: '저장',
    saved: '저장했어요',
    factoryName: '공장 이름 (결제 뒤 고객에게 보임)',
    anonRegion: '공개 지역',
    categories: '제품 분류',
    type: '샘플 방식',
    status: '상태',
    offerStatus: { draft: '초안', active: '공개', paused: '멈춤' },
    currency: '통화',
    price: '가격',
    priceHint: '유료 시제품·컨설팅·소분 키트만. 무료 방식은 레시피캣 연결 수수료를 받아요',
    leadDays: '소요일 (결제 뒤 발송·일정까지)',
    credited: '본 발주 때 차감',
    requiredInfo: '결제 뒤 받을 고객 정보',
    tastingRegions: '시식 지역 (쉼표로)',
    tastingWeekdays: '시식 요일',
    consultingMinutes: '컨설팅 시간 (분)',
    deliverables: '받는 것 (수량·용량·결과물)',
    note: '메모',
    publicTextHint:
      '받는 것·메모·시식 지역은 결제 전에도 고객에게 보여요. 공장 이름·연락처·주소는 쓰지 마세요(자동으로 가려져요).',
    freeCostTitle: '무료 제공 비용',
    freeCostHint:
      '무료 방식은 기본이 아니에요. 공장이 무료로 줄 때만, 누가 비용을 내는지와 한도(금액이나 수량)를 적어 주세요.',
    costBearer: '비용 부담',
    budgetAmount: '예산 금액',
    budgetQuantity: '최대 제공 건수',
    freeCostNote: '비용 메모 (고객에게 안 보임)',
    ordersTitle: '샘플 주문',
    noOrders: '아직 주문이 없어요.',
    customer: (ref) => `고객 #${ref}`,
    brief: '고객 요청',
    due: '기한',
    specLocked: '결제가 확인되면 사양서와 고객이 동의한 정보가 열려요.',
    formulation: '배합',
    process: '공정',
    cost: '원가 가정 (낱개)',
    downloadCsv: 'CSV 내려받기',
    downloadJson: 'JSON 내려받기',
    disclosed: '고객이 동의한 정보',
    progressNote: '진행 메모 (송장·장소 등, 고객에게 보임)',
    scheduledAt: '일시 (시식·컨설팅)',
    recordProgress: '발송·일정 기록',
    failed: '처리하지 못했어요. 다시 시도해 주세요.',
    messagesTitle: '고객과 대화',
    messagesNote:
      '고객과는 레시피캣이 이어 드려요. 보낸 글은 AI 가 고객 조건에 맞춰 정리해 전달하고, 연락처·링크는 가려져요.',
    messagePlaceholder: '고객에게 전할 말 (배합·공정·가격·일정)',
    counterOffer: '역제안으로 보내기',
    send: '보내기',
    changes: '반영할 점',
  },
  en: {
    section: 'Samples & prototypes',
    scope:
      'Post your sample options. Customers see you under an anonymous name until they pay, then see your factory name. RecipeCat relays the conversation.',
    offersTitle: 'Sample offers',
    newOffer: 'New offer',
    edit: 'Edit',
    save: 'Save',
    saved: 'Saved',
    factoryName: 'Factory name (shown to the customer after payment)',
    anonRegion: 'Public region',
    categories: 'Product categories',
    type: 'Sample type',
    status: 'Status',
    offerStatus: { draft: 'Draft', active: 'Live', paused: 'Paused' },
    currency: 'Currency',
    price: 'Price',
    priceHint:
      'Paid prototype, consulting and portion kit only. Free types carry a RecipeCat connection fee',
    leadDays: 'Lead time in days (payment to shipping or scheduling)',
    credited: 'Credited toward the production order',
    requiredInfo: 'Customer details you need after payment',
    tastingRegions: 'Tasting regions (comma separated)',
    tastingWeekdays: 'Tasting days',
    consultingMinutes: 'Consulting time (minutes)',
    deliverables: 'What the customer gets (quantity, size, deliverables)',
    note: 'Note',
    publicTextHint:
      'What the customer gets, notes and tasting regions are shown before payment. Do not write your factory name, contacts or address (they are masked automatically).',
    freeCostTitle: 'Free offer costs',
    freeCostHint:
      'Free options are not the default. Only when your factory offers it for free, record who pays and the limit (amount or quantity).',
    costBearer: 'Paid by',
    budgetAmount: 'Budget amount',
    budgetQuantity: 'Maximum number provided',
    freeCostNote: 'Cost note (not shown to customers)',
    ordersTitle: 'Sample orders',
    noOrders: 'No orders yet.',
    customer: (ref) => `Customer #${ref}`,
    brief: 'Customer request',
    due: 'Due',
    specLocked:
      'The spec and the details the customer agreed to share open once payment is confirmed.',
    formulation: 'Formulation',
    process: 'Process',
    cost: 'Cost assumptions (per unit)',
    downloadCsv: 'Download CSV',
    downloadJson: 'Download JSON',
    disclosed: 'Details the customer agreed to share',
    progressNote: 'Progress note (tracking number, place; shown to the customer)',
    scheduledAt: 'Date and time (tasting or consulting)',
    recordProgress: 'Record shipping or schedule',
    failed: 'That did not work. Please try again.',
    messagesTitle: 'Talk with the customer',
    messagesNote:
      'RecipeCat connects you with the customer. AI lines up what you send against the customer’s conditions before delivering it. Contact details and links are hidden.',
    messagePlaceholder: 'Message to the customer (formula, process, price, schedule)',
    counterOffer: 'Send as a counter-offer',
    send: 'Send',
    changes: 'What to apply',
  },
  ja: {
    section: 'サンプル・試作',
    scope:
      'サンプルの方式と条件を掲載すると、お客様はお支払いまで匿名名で、お支払い後に工場名を見ます。お客様とのやり取りはレシピキャットがつなぎます。',
    offersTitle: 'サンプル提案',
    newOffer: '新しい提案',
    edit: '編集',
    save: '保存',
    saved: '保存しました',
    factoryName: '工場名 (お支払い後にお客様へ表示)',
    anonRegion: '公開地域',
    categories: '製品カテゴリ',
    type: 'サンプル方式',
    status: '状態',
    offerStatus: { draft: '下書き', active: '公開', paused: '停止' },
    currency: '通貨',
    price: '価格',
    priceHint:
      '有料試作・コンサル・小分けキットのみ。無料方式はレシピキャットの仲介手数料がかかります',
    leadDays: '所要日数 (お支払いから発送・日程まで)',
    credited: '本発注時に差し引く',
    requiredInfo: 'お支払い後に受け取るお客様情報',
    tastingRegions: '試食地域 (カンマ区切り)',
    tastingWeekdays: '試食曜日',
    consultingMinutes: 'コンサル時間 (分)',
    deliverables: 'お客様が受け取るもの (数量・容量・成果物)',
    note: 'メモ',
    publicTextHint:
      '受け取るもの・メモ・試食地域はお支払い前にもお客様に表示されます。工場名・連絡先・住所は書かないでください(自動で隠されます)。',
    freeCostTitle: '無料提供の費用',
    freeCostHint:
      '無料の方式は標準ではありません。工場が無料で提供する場合のみ、費用の負担者と上限(金額または数量)を記入してください。',
    costBearer: '費用負担',
    budgetAmount: '予算金額',
    budgetQuantity: '最大提供件数',
    freeCostNote: '費用メモ (お客様には表示されません)',
    ordersTitle: 'サンプル注文',
    noOrders: 'まだ注文はありません。',
    customer: (ref) => `お客様 #${ref}`,
    brief: 'お客様の要望',
    due: '期限',
    specLocked: 'お支払いが確認されると、仕様書とお客様が同意した情報が表示されます。',
    formulation: '配合',
    process: '工程',
    cost: '原価の前提 (1個あたり)',
    downloadCsv: 'CSV をダウンロード',
    downloadJson: 'JSON をダウンロード',
    disclosed: 'お客様が同意した情報',
    progressNote: '進行メモ (送り状番号・場所など。お客様に表示)',
    scheduledAt: '日時 (試食・コンサル)',
    recordProgress: '発送・日程を記録',
    failed: '処理できませんでした。もう一度お試しください。',
    messagesTitle: 'お客様とのやり取り',
    messagesNote:
      'お客様とはレシピキャットがおつなぎします。送ったメッセージは AI がお客様の条件に合わせて整理してから届けます。連絡先やリンクは隠されます。',
    messagePlaceholder: 'お客様へのメッセージ (配合・工程・価格・日程)',
    counterOffer: '逆提案として送る',
    send: '送信',
    changes: '反映する点',
  },
  zh: {
    section: '样品与试制',
    scope:
      '发布样品方式与条件后，客户付款前只看到匿名名称，付款后才看到工厂名称。与客户的沟通由 RecipeCat 转达。',
    offersTitle: '样品方案',
    newOffer: '新方案',
    edit: '编辑',
    save: '保存',
    saved: '已保存',
    factoryName: '工厂名称（付款后向客户显示）',
    anonRegion: '公开地区',
    categories: '产品类别',
    type: '样品方式',
    status: '状态',
    offerStatus: { draft: '草稿', active: '已发布', paused: '已暂停' },
    currency: '币种',
    price: '价格',
    priceHint: '仅限付费试制、咨询和分装套装。免费方式收取 RecipeCat 撮合服务费',
    leadDays: '所需天数（付款到发货或安排）',
    credited: '正式下单时抵扣',
    requiredInfo: '付款后需要的客户信息',
    tastingRegions: '试吃地区（用逗号分隔）',
    tastingWeekdays: '试吃日',
    consultingMinutes: '咨询时长（分钟）',
    deliverables: '客户获得的内容（数量、规格、成果）',
    note: '备注',
    publicTextHint:
      '客户获得的内容、备注和试吃地区在付款前也会显示给客户。请勿填写工厂名称、联系方式或地址（会被自动隐藏）。',
    freeCostTitle: '免费提供的费用',
    freeCostHint:
      '免费方式不是默认选项。仅在工厂免费提供时，填写由谁承担费用及上限（金额或数量）。',
    costBearer: '费用承担方',
    budgetAmount: '预算金额',
    budgetQuantity: '最多提供次数',
    freeCostNote: '费用备注（客户不可见）',
    ordersTitle: '样品订单',
    noOrders: '暂无订单。',
    customer: (ref) => `客户 #${ref}`,
    brief: '客户要求',
    due: '期限',
    specLocked: '确认付款后，规格书和客户同意提供的信息才会显示。',
    formulation: '配方',
    process: '工艺',
    cost: '成本假设（单件）',
    downloadCsv: '下载 CSV',
    downloadJson: '下载 JSON',
    disclosed: '客户同意提供的信息',
    progressNote: '进度说明（运单号、地点等，客户可见）',
    scheduledAt: '日期时间（试吃或咨询）',
    recordProgress: '记录发货或安排',
    failed: '未能完成，请重试。',
    messagesTitle: '与客户沟通',
    messagesNote:
      'RecipeCat 为您连接客户。您发送的内容会由 AI 按客户条件整理后送达，联系方式和链接会被隐藏。',
    messagePlaceholder: '给客户的留言（配方、工艺、价格、时间）',
    counterOffer: '作为反提案发送',
    send: '发送',
    changes: '需调整之处',
  },
}
